/**
 * First-party anonymous visitor id — and the consent that legally gates it.
 *
 * A long-lived unique identifier stored on the device for analytics /
 * attribution needs opt-in consent in the EU (ePrivacy, RO Law 506/2004),
 * even when it's first-party and never leaves our stack. So:
 *
 *   - `resto_consent` remembers the visitor's choice ("granted" / "denied");
 *     storing the choice itself is strictly necessary and needs no consent.
 *   - `resto_anon_id` (a uuid) is minted lazily, ONLY while consent is
 *     granted. Declining — or later withdrawing (Settings → "Forget this
 *     device", `src/lib/deviceData.ts`) — removes it.
 *
 * The id powers guest→account continuity: on sign-in/up the pair
 * (anon_id, user_id) is linked server-side via the `link_anon_id` RPC
 * (schema.sql §18), so conversion attribution survives account creation.
 * It is NOT sent anywhere else in this update (the analytics pipeline that
 * will attach it to events is a future part).
 *
 * Everything here is fail-silent: blocked storage (private mode) simply means
 * no id, and every caller treats `null` as "feature off".
 */

export type ConsentState = "granted" | "denied" | "unset";

const CONSENT_KEY = "resto_consent";
const ANON_ID_KEY = "resto_anon_id";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readConsent(): ConsentState {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw === "granted" || raw === "denied" ? raw : "unset";
  } catch {
    return "unset";
  }
}

/** Persist the visitor's choice. Denying also erases any previously minted id. */
export function setConsent(granted: boolean): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
    if (!granted) window.localStorage.removeItem(ANON_ID_KEY);
  } catch {
    // Private mode / storage full — the notice will simply re-appear.
  }
}

/** The stored anon id, if any — never mints one (unlike getAnonId). */
export function readAnonId(): string | null {
  try {
    const existing = window.localStorage.getItem(ANON_ID_KEY);
    return existing && UUID_RE.test(existing) ? existing : null;
  } catch {
    return null;
  }
}

/**
 * Forget the choice AND the id (after account deletion): the notice asks
 * afresh on the next feed visit.
 */
export function resetConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
    window.localStorage.removeItem(ANON_ID_KEY);
  } catch {
    // Storage unavailable — nothing was stored either.
  }
}

/**
 * The visitor's anonymous id, minting it on first use — or `null` when
 * consent isn't granted (or storage is unavailable). Never prompts.
 */
export function getAnonId(): string | null {
  if (readConsent() !== "granted") return null;
  try {
    const existing = window.localStorage.getItem(ANON_ID_KEY);
    if (existing && UUID_RE.test(existing)) return existing;
    const minted = crypto.randomUUID();
    window.localStorage.setItem(ANON_ID_KEY, minted);
    return minted;
  } catch {
    return null;
  }
}
